class MegaMenu extends HTMLElement {
  constructor() {
    super();

    this.items = this.querySelectorAll('.m-mega-menu__item-hover');
    this.overlayMegaMenu = this.querySelectorAll('.m-mega-menu__overlay');
    this.drawerOverlay = document.querySelector('[data-drawer-overlay]');
    this.fastDeliveryCache = new Map();
    this.dropdownHideTimers = new WeakMap();
  }

  connectedCallback() {
    this.items.forEach((item) => {
      const trigger = item.querySelector('[data-open-mega-menu]');
      const dropdown = item.querySelector('.m-mega-menu__additional-menu');

      if (!trigger || !dropdown) return;

      dropdown.hidden = true;

      trigger.addEventListener('mouseenter', () => {
        this.closeAllMenus(item);
        this.updateHeaderBottom();

        this.openDropdown(item, dropdown);

        document.body.classList.add('mega-menu-open');
        this.showOverlay();

        this.refreshDropdownSliders(dropdown);
        this.loadFastDeliverySizes(item);
      });

      item.addEventListener('mouseleave', () => {
        this.closeDropdown(item, dropdown);

        document.body.classList.remove('mega-menu-open');
        this.hideOverlay();
      });

      // Fast delivery sizes load on first hover (mouseenter above); fetching every
      // collection page on page load cost ~240KB and seconds of main-thread time.
    });

    this.drawerOverlay?.addEventListener('click', () => {
      if (document.body.classList.contains('mega-menu-open')) {
        this.closeAllMenus();
      }
    });

    this.overlayMegaMenu.forEach((item) => {
      item.addEventListener('click', () => {
        this.closeAllMenus();
      });
    });
  }

  openDropdown(item, dropdown) {
    const hideTimer = this.dropdownHideTimers.get(dropdown);

    if (hideTimer) {
      clearTimeout(hideTimer);
      this.dropdownHideTimers.delete(dropdown);
    }

    dropdown.hidden = false;
    void dropdown.offsetHeight;

    item.classList.add('is-open');
    dropdown.classList.add('is-open');
  }

  closeDropdown(item, dropdown) {
    const hideTimer = this.dropdownHideTimers.get(dropdown);

    if (hideTimer) {
      clearTimeout(hideTimer);
    }

    item.classList.remove('is-open');
    dropdown.classList.remove('is-open');

    const nextHideTimer = window.setTimeout(() => {
      if (!dropdown.classList.contains('is-open')) {
        dropdown.hidden = true;
      }

      this.dropdownHideTimers.delete(dropdown);
    }, 220);

    this.dropdownHideTimers.set(dropdown, nextHideTimer);
  }

  closeAllMenus(exceptItem = null) {
    this.items.forEach((item) => {
      if (item === exceptItem) return;

      const dropdown = item.querySelector('.m-mega-menu__additional-menu');

      if (dropdown) {
        this.closeDropdown(item, dropdown);
      }
    });

    document.body.classList.remove('mega-menu-open');
    this.hideOverlay();
  }

  showOverlay() {
    this.drawerOverlay?.classList.add('show-overlay');
  }

  hideOverlay() {
    if (document.body.classList.contains('m-cart-drawer-open')) return;
    if (document.body.classList.contains('m-size-guide-open')) return;

    this.drawerOverlay?.classList.remove('show-overlay');
  }

  updateHeaderBottom() {
    const header = document.querySelector('header');
    if (!header) return;

    document.documentElement.style.setProperty(
      '--m-header-bottom',
      `${Math.max(0, header.getBoundingClientRect().bottom)}px`
    );
  }

  refreshDropdownSliders(dropdown) {
    const sliders = dropdown.querySelectorAll('swiper-slider');

    if (!sliders.length) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        sliders.forEach((slider) => {
          if (typeof slider.initializeSwiper === 'function') {
            slider.initializeSwiper();
          }

          if (!slider.swiper || slider.swiper.destroyed) return;

          slider.swiper.update();
          slider.swiper.scrollbar?.updateSize?.();
          slider.swiper.navigation?.update?.();

          if (typeof slider.toggleNavigation === 'function') {
            slider.toggleNavigation();
          }
        });
      });
    });
  }

  async loadFastDeliverySizes(item) {
    const wrapper = item.querySelector('[data-mega-fast-delivery]');
    const sizesWrapper = item.querySelector('[data-mega-fast-delivery-sizes]');

    if (!wrapper || !sizesWrapper) return;

    const collectionUrl = wrapper.dataset.collectionUrl;

    if (!collectionUrl || wrapper.dataset.loaded === 'true') return;

    wrapper.dataset.loaded = 'true';

    try {
      const sizes = await this.getFastDeliverySizes(collectionUrl);

      if (!sizes.length) return;

      this.renderFastDeliverySizes(wrapper, sizesWrapper, collectionUrl, sizes);
    } catch (e) {
      wrapper.dataset.loaded = 'false';
      console.error('Mega menu fast delivery error:', e);
    }
  }

  async getFastDeliverySizes(collectionUrl) {
    const requestUrl = this.buildFastDeliveryUrl(collectionUrl);
    const cacheKey = requestUrl.pathname + requestUrl.search;

    if (this.fastDeliveryCache.has(cacheKey)) {
      return this.fastDeliveryCache.get(cacheKey);
    }

    const res = await fetch(cacheKey);
    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const sizes = [];
    const addedSizes = new Set();

    doc.querySelectorAll('[data-filters-size-label]').forEach((label) => {
      const input = label.querySelector('input[name][value]');
      const sizeLabel = label.dataset.filtersSizeLabel;

      if (!input || !sizeLabel) return;

      const sizeKey = `${input.name}:${input.value}`;

      if (addedSizes.has(sizeKey)) return;

      sizes.push({
        label: sizeLabel,
        paramName: input.name,
        value: input.value,
      });

      addedSizes.add(sizeKey);
    });

    this.fastDeliveryCache.set(cacheKey, sizes);

    return sizes;
  }

  renderFastDeliverySizes(wrapper, sizesWrapper, collectionUrl, sizes) {
    sizesWrapper.replaceChildren();

    const showEuPrefix = wrapper.dataset.collectionId === '404161757424';

    sizes.forEach((size) => {
      const linkUrl = this.buildFastDeliveryUrl(collectionUrl);
      const link = document.createElement('a');

      linkUrl.searchParams.set(size.paramName, size.value);

      link.href = linkUrl.pathname + linkUrl.search;
      link.className = 'm-mega-menu__fast-delivery-size';
      link.textContent = this.formatFastDeliverySizeLabel(size.label, showEuPrefix);

      sizesWrapper.appendChild(link);
    });

    wrapper.hidden = false;
    this.setupFastDeliveryScrollFade(sizesWrapper);
  }

  setupFastDeliveryScrollFade(sizesWrapper) {
    const updateScrollFade = () => {
      sizesWrapper.classList.toggle('is-scrolled-start', sizesWrapper.scrollLeft > 0);
    };

    updateScrollFade();

    if (sizesWrapper.dataset.scrollFadeBound === 'true') return;

    sizesWrapper.dataset.scrollFadeBound = 'true';
    sizesWrapper.addEventListener('scroll', updateScrollFade, { passive: true });
    window.addEventListener('resize', updateScrollFade);
  }

  formatFastDeliverySizeLabel(label, showEuPrefix) {
    if (!showEuPrefix) return label;

    return label.trim().toUpperCase().startsWith('EU ') ? label : `EU ${label}`;
  }

  buildFastDeliveryUrl(collectionUrl) {
    const url = new URL(collectionUrl, location.origin);

    url.searchParams.delete('page');
    url.searchParams.set('filter.v.m.custom.fast_delivery', '1');

    return url;
  }
}

customElements.define('mega-menu', MegaMenu);
